import React from 'react';
import { motion } from 'framer-motion';
import { FaHandshake, FaUsers, FaTrophy, FaGraduationCap, FaGlobe, FaExternalLinkAlt } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import './ValenciaPartnership.css';

const ValenciaPartnership: React.FC = () => {
  const { t } = useLanguage();

  const benefits = [
    {
      icon: <FaGraduationCap />,
      title: t('valenciaPartnership.benefits.training.title'),
      description: t('valenciaPartnership.benefits.training.description')
    },
    {
      icon: <FaUsers />,
      title: t('valenciaPartnership.benefits.coaches.title'),
      description: t('valenciaPartnership.benefits.coaches.description')
    },
    {
      icon: <FaTrophy />,
      title: t('valenciaPartnership.benefits.methodology.title'),
      description: t('valenciaPartnership.benefits.methodology.description')
    },
    {
      icon: <FaGlobe />,
      title: t('valenciaPartnership.benefits.exchange.title'),
      description: t('valenciaPartnership.benefits.exchange.description')
    }
  ];

  return (
    <section id="valencia-partnership" className="valencia-partnership section-lg">
      <div className="container">
        <motion.div
          className="valencia-partnership__header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="valencia-partnership__title">{t('valenciaPartnership.title')}</h2>
          <p className="valencia-partnership__subtitle">{t('valenciaPartnership.subtitle')}</p>
          <a 
            href="https://www.valenciacf.com/valencia-cf-new-technical-partner-of-fk-junior-of-serbia" 
            target="_blank" 
            rel="noopener noreferrer"
            className="valencia-partnership__official-link"
          >
            {t('valenciaPartnership.officialAnnouncement')}
            <FaExternalLinkAlt className="valencia-partnership__link-icon" />
          </a>
        </motion.div>

        <motion.div
          className="valencia-partnership__gallery"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          viewport={{ once: true }}
        >
          <div className="valencia-partnership__gallery-grid">
            <div className="valencia-partnership__gallery-item">
              <img 
                src="/images/valenciajunior.jpg" 
                alt="Valencia CF Partnership"
                className="valencia-partnership__gallery-img"
              />
            </div>
            <div className="valencia-partnership__gallery-item">
              <img 
                src="/images/valenciajunior2.jpg" 
                alt="Valencia CF Partnership"
                className="valencia-partnership__gallery-img"
              />
            </div>
            <div className="valencia-partnership__gallery-item">
              <img 
                src="/images/valenciajunior3.jpg" 
                alt="Valencia CF Partnership"
                className="valencia-partnership__gallery-img"
              />
            </div>
          </div>
        </motion.div>

        <motion.div
          className="valencia-partnership__intro"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="valencia-partnership__intro-icon">
            <FaHandshake />
          </div>
          <p className="valencia-partnership__intro-text">{t('valenciaPartnership.intro')}</p>
        </motion.div>

        <div className="valencia-partnership__benefits">
          <h3 className="valencia-partnership__benefits-title">{t('valenciaPartnership.benefits.title')}</h3>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="benefit-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                <div className="benefit-card__icon">{benefit.icon}</div>
                <h4 className="benefit-card__title">{benefit.title}</h4>
                <p className="benefit-card__description">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div
          className="valencia-partnership__future"
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.45 }}
          viewport={{ once: true }}
        >
          <span className="valencia-partnership__future-glow" aria-hidden />
          <div className="valencia-partnership__future-inner">
            <h3 className="future-title">{t('valenciaPartnership.future.title')}</h3>
            <span className="future-divider" aria-hidden />
            <p className="future-text">{t('valenciaPartnership.future.description')}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ValenciaPartnership;

